if (!customElements.get('vl-pdp-expert-video-reviews')) {
  class VLPDPExpertVideoReviews extends HTMLElement {
    constructor() {
      super();

      this.isInitialized = false;
      this.currentIndex = 0;
      this.scrollFrame = null;
      this.abortController = null;
      this.iframeObserver = null;
      this.resizeObserver = null;

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleViewportScroll = this.handleViewportScroll.bind(this);
      this.handleViewportKeydown = this.handleViewportKeydown.bind(this);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-vl-pdp-expert-reviews-viewport]');
      this.track = this.querySelector('[data-vl-pdp-expert-reviews-track]');
      this.slides = [...this.querySelectorAll('[data-vl-pdp-expert-reviews-slide]')];
      this.previousButton = this.querySelector('[data-vl-pdp-expert-reviews-previous]');
      this.nextButton = this.querySelector('[data-vl-pdp-expert-reviews-next]');
      this.dots = [...this.querySelectorAll('[data-vl-pdp-expert-reviews-dot]')];

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', this.handlePreviousClick, { signal });
      this.nextButton?.addEventListener('click', this.handleNextClick, { signal });
      this.viewport.addEventListener('scroll', this.handleViewportScroll, { passive: true, signal });
      this.viewport.addEventListener('keydown', this.handleViewportKeydown, { signal });
      this.addEventListener('click', this.handlePaginationClick, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      this.initIframeLoading();
      this.initResizeObserver();
      this.updateLayoutMetrics();
      this.updateState();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.abortController?.abort();
      this.iframeObserver?.disconnect();
      this.resizeObserver?.disconnect();

      if (this.scrollFrame !== null) {
        cancelAnimationFrame(this.scrollFrame);
      }

      this.abortController = null;
      this.iframeObserver = null;
      this.resizeObserver = null;
      this.scrollFrame = null;
      this.isInitialized = false;
    }

    initIframeLoading() {
      const iframes = [...this.querySelectorAll('[data-vl-pdp-expert-reviews-iframe][data-src]')];
      if (iframes.length === 0) return;

      if (!('IntersectionObserver' in window)) {
        iframes.forEach((iframe) => this.loadIframe(iframe));
        return;
      }

      this.iframeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.loadIframe(entry.target);
            this.iframeObserver?.unobserve(entry.target);
          });
        },
        {
          root: this.viewport,
          rootMargin: '0px 25% 0px 25%',
          threshold: 0.01,
        }
      );

      iframes.forEach((iframe) => this.iframeObserver.observe(iframe));
    }

    loadIframe(iframe) {
      const source = iframe.dataset.src;
      if (!source || iframe.src === source) return;

      iframe.src = source;
      iframe.removeAttribute('data-src');
    }

    initResizeObserver() {
      if (!('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.viewport);
      const firstMedia = this.slides[0]?.querySelector('[data-vl-pdp-expert-reviews-media]');
      if (firstMedia) this.resizeObserver.observe(firstMedia);
    }

    handleResize() {
      this.updateLayoutMetrics();
      this.scrollToIndex(this.currentIndex, false);
    }

    updateLayoutMetrics() {
      const firstMedia = this.slides[0]?.querySelector('[data-vl-pdp-expert-reviews-media]');
      if (firstMedia) {
        this.style.setProperty(
          '--vl-pdp-expert-reviews-media-height',
          `${firstMedia.getBoundingClientRect().height}px`
        );
      }

      this.updateState();
    }

    handlePreviousClick() {
      const maxIndex = this.getMaxIndex();
      const nextIndex = this.currentIndex <= 0 ? maxIndex : this.currentIndex - 1;
      this.scrollToIndex(nextIndex);
    }

    handleNextClick() {
      const maxIndex = this.getMaxIndex();
      const nextIndex = this.currentIndex >= maxIndex ? 0 : this.currentIndex + 1;
      this.scrollToIndex(nextIndex);
    }

    handleViewportScroll() {
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.currentIndex = this.getNearestIndex();
        this.updateState();
      });
    }

    handleViewportKeydown(event) {
      if (event.target !== this.viewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePreviousClick();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNextClick();
      }
    }

    handlePaginationClick(event) {
      const dot = event.target.closest('[data-vl-pdp-expert-reviews-dot]');
      if (!dot || !this.contains(dot)) return;

      this.scrollToIndex(Number(dot.dataset.slideIndex));
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (slideIndex === -1) return;

      this.scrollToIndex(slideIndex, false);
    }

    scrollToIndex(index, animate = true) {
      const targetIndex = Math.max(0, Math.min(index, this.getMaxIndex()));
      const targetSlide = this.slides[targetIndex];
      if (!targetSlide) return;

      this.currentIndex = targetIndex;
      this.viewport.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: animate && !this.prefersReducedMotion ? 'smooth' : 'auto',
      });
      this.updateState();
    }

    getNearestIndex() {
      if (this.slides.length <= 1) return 0;

      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return Math.min(nearestIndex, this.getMaxIndex());
    }

    getVisibleCount() {
      if (window.matchMedia('(min-width: 1024px)').matches) return 3;
      if (window.matchMedia('(min-width: 768px)').matches) return 2;
      return 1;
    }

    getMaxIndex() {
      return Math.max(0, this.slides.length - this.getVisibleCount());
    }

    updateState() {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        dot.classList.toggle('is-active', isActive);

        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('vl-pdp-expert-video-reviews', VLPDPExpertVideoReviews);
}
